/**
 * The Discipline page's view model: one row per project with its newest report, score
 * history and status, the fleet summary, and violations grouped by rule. Pure functions.
 */
import { disciplineRuleInfo } from '@orgus/protocol';
import type { DisciplineReport, DisciplineRuleInfo, DisciplineScorePoint, DisciplineState, Id, Machine, Project, Violation } from '@orgus/protocol';
import type { StatusTone } from '@orgus/ui';

export type RepoHealth = 'healthy' | 'attention' | 'failing' | 'error' | 'pending';

export const HEALTH_LABEL: Record<RepoHealth, string> = {
  healthy: 'Healthy',
  attention: 'Needs attention',
  failing: 'Failing',
  error: 'Check failed',
  pending: 'Waiting for first check',
};

export const HEALTH_TONE: Record<RepoHealth, StatusTone> = {
  healthy: 'passed',
  attention: 'waiting',
  failing: 'failed',
  error: 'failed',
  pending: 'queued',
};

export function healthOfScore(score: number): RepoHealth {
  if (score >= 90) return 'healthy';
  if (score >= 70) return 'attention';
  return 'failing';
}

export function healthOf(report: DisciplineReport | null | undefined): RepoHealth {
  if (!report) return 'pending';
  if (report.error) return 'error';
  return healthOfScore(report.score);
}

export interface RepoRow {
  projectId: Id;
  name: string;
  report: DisciplineReport | null;
  /** Oldest first. */
  history: DisciplineScorePoint[];
  health: RepoHealth;
  errors: number;
  warns: number;
  machineName: string | null;
}

function counts(r: DisciplineReport | null): { errors: number; warns: number } {
  if (!r) return { errors: 0, warns: 0 };
  if (r.rules?.length) return r.rules.reduce((a, x) => ({ errors: a.errors + x.errors, warns: a.warns + x.warns }), { errors: 0, warns: 0 });
  return { errors: r.violations.filter((v) => v.severity === 'error').length, warns: r.violations.filter((v) => v.severity === 'warn').length };
}

const ORDER: Record<RepoHealth, number> = { error: 0, failing: 1, attention: 2, pending: 3, healthy: 4 };

/** Every project, worst first; projects with no report yet are listed as pending. */
export function repoRows(projects: readonly Project[], state: DisciplineState | undefined, machines: Record<Id, Machine>): RepoRow[] {
  const newest = new Map<Id, DisciplineReport>();
  for (const r of state?.reports ?? []) {
    const prev = newest.get(r.projectId);
    if (!prev || r.checkedAt > prev.checkedAt) newest.set(r.projectId, r);
  }
  const rows: RepoRow[] = projects.map((p) => {
    const report = newest.get(p.id) ?? null;
    return {
      projectId: p.id,
      name: p.name,
      report,
      history: state?.history?.[p.id] ?? [],
      health: healthOf(report),
      ...counts(report),
      machineName: report ? (machines[report.machineId]?.name ?? null) : null,
    };
  });
  return rows.sort((a, b) => ORDER[a.health] - ORDER[b.health] || (a.report?.score ?? 101) - (b.report?.score ?? 101) || a.name.localeCompare(b.name));
}

export interface FleetSummary {
  /** Average score over checked repos, or null when none is checked. */
  score: number | null;
  checked: number;
  total: number;
  /** Repos below 90 or whose check failed. */
  needAttention: number;
  errors: number;
  warns: number;
  waived: number;
  /** Change of the average since the oldest point in the visible history, or null. */
  trend: number | null;
}

export function fleetSummary(rows: readonly RepoRow[]): FleetSummary {
  const checked = rows.filter((r) => r.report && !r.report.error);
  const score = checked.length ? Math.round(checked.reduce((n, r) => n + r.report!.score, 0) / checked.length) : null;
  // A trend needs history: every checked repo has a first point and at least one has moved since.
  const firsts = checked.map((r) => r.history[0]?.score).filter((x): x is number => typeof x === 'number');
  const hasHistory = checked.some((r) => r.history.length > 1);
  const trend = score !== null && hasHistory && firsts.length === checked.length ? score - Math.round(firsts.reduce((a, b) => a + b, 0) / firsts.length) : null;
  return {
    score,
    checked: checked.length,
    total: rows.length,
    needAttention: rows.filter((r) => r.health === 'attention' || r.health === 'failing' || r.health === 'error').length,
    errors: rows.reduce((n, r) => n + r.errors, 0),
    warns: rows.reduce((n, r) => n + r.warns, 0),
    waived: rows.reduce((n, r) => n + (r.report?.waived ?? 0), 0),
    trend,
  };
}

export interface RuleGroup {
  rule: string;
  info: DisciplineRuleInfo;
  errors: number;
  warns: number;
  waived: number;
  /** The violations the report carries (may be fewer than errors + warns when truncated). */
  violations: Violation[];
}

/** Violations grouped by rule, errors first, then the rules that pass. */
export function ruleGroups(report: DisciplineReport): { failing: RuleGroup[]; passing: RuleGroup[] } {
  const byRule = new Map<string, RuleGroup>();
  const get = (rule: string) => {
    let g = byRule.get(rule);
    if (!g) {
      g = { rule, info: disciplineRuleInfo(rule), errors: 0, warns: 0, waived: 0, violations: [] };
      byRule.set(rule, g);
    }
    return g;
  };
  for (const s of report.rules ?? []) Object.assign(get(s.rule), { errors: s.errors, warns: s.warns, waived: s.waived });
  for (const v of report.violations) get(v.rule).violations.push(v);
  if (!report.rules?.length) {
    for (const g of byRule.values()) {
      g.errors = g.violations.filter((v) => v.severity === 'error').length;
      g.warns = g.violations.length - g.errors;
    }
  }
  const all = [...byRule.values()];
  const failing = all.filter((g) => g.errors + g.warns > 0).sort((a, b) => b.errors - a.errors || b.warns - a.warns || a.rule.localeCompare(b.rule));
  const passing = all.filter((g) => g.errors + g.warns === 0).sort((a, b) => a.rule.localeCompare(b.rule));
  return { failing, passing };
}

export function rulebookLabel(r: DisciplineReport): string {
  if (r.rulebook === 'repo') return r.pack ? `Own rulebook, extends orgus/${r.pack}` : 'Own rulebook';
  return 'Built-in recommended pack';
}
